/**
 * Branding - Upload custom logo (3 variants) + intro video (2 variants)
 * Multi-platform: each variant for different device sizes
 */
import React, { useEffect, useState, useRef } from "react";
import {
  getBranding,
  uploadLogo,
  uploadIntro,
  deleteLogo,
  deleteIntro,
  updateAppName,
  getAssetUrl,
  getSettings,
  updateSettings,
} from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, Image as ImageIcon, Video, Save, Sparkles, Palette, FileBox } from "lucide-react";
import { ADMIN } from "../lib/testIds";

const logoVariants = [
  { key: "desktop", label: "Desktop Logo", hint: "Recommended: 1024×1024px, PNG/SVG" },
  { key: "mobile", label: "Mobile Logo", hint: "Recommended: 512×512px, PNG" },
  { key: "icon", label: "App Icon", hint: "Recommended: 256×256px, PNG" },
];

const introVariants = [
  { key: "desktop", label: "Desktop Intro Video", hint: "Recommended: 1920×1080, MP4, <30s" },
  { key: "mobile", label: "Mobile Intro Video", hint: "Recommended: 1080×1920 (portrait), MP4" },
];

function AssetCard({ title, hint, url, type, onUpload, onDelete, testId, uploading }) {
  const fileRef = useRef(null);

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="font-semibold text-sm">{title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
          </div>
          {url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-red-600 hover:bg-red-50 -mt-1 -mr-1"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        <div className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center mb-3 overflow-hidden">
          {url ? (
            type === "image" ? (
              <img src={url} alt={title} className="max-h-full max-w-full object-contain" />
            ) : (
              <video src={url} className="max-h-full max-w-full" controls />
            )
          ) : (
            <div className="text-slate-400 text-center">
              {type === "image" ? (
                <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-50" />
              ) : (
                <Video className="w-8 h-8 mx-auto mb-1 opacity-50" />
              )}
              <span className="text-xs">No file uploaded</span>
            </div>
          )}
        </div>

        <input
          ref={fileRef}
          type="file"
          accept={type === "image" ? "image/*" : "video/*"}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = "";
          }}
        />
        <Button
          data-testid={testId}
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full"
        >
          <Upload className="w-3.5 h-3.5 mr-2" />
          {uploading ? "Uploading..." : url ? "Replace" : "Upload"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Branding() {
  const { toast } = useToast();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [appName, setAppName] = useState("");
  const [panelName, setPanelName] = useState("");
  const [savingPanel, setSavingPanel] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([getBranding(), getSettings()]);
      setConfig(c);
      setAppName(c.app_name || "ZA Player Pro");
      setPanelName(s.panel_name || "ZA Player Pro Admin");
    } catch (e) {
      toast({ title: "Failed to load", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleLogoUpload = async (variant, file) => {
    setUploading(`logo-${variant}`);
    try {
      await uploadLogo(variant, file);
      toast({ title: `Logo (${variant}) uploaded` });
      refresh();
    } catch (e) {
      toast({
        title: "Upload failed",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setUploading(null);
    }
  };

  const handleIntroUpload = async (variant, file) => {
    setUploading(`intro-${variant}`);
    try {
      await uploadIntro(variant, file);
      toast({ title: `Intro video (${variant}) uploaded` });
      refresh();
    } catch (e) {
      toast({
        title: "Upload failed",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setUploading(null);
    }
  };

  const handleLogoDelete = async (variant) => {
    try {
      await deleteLogo(variant);
      toast({ title: "Logo removed" });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleIntroDelete = async (variant) => {
    try {
      await deleteIntro(variant);
      toast({ title: "Intro removed" });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleSaveAppName = async () => {
    try {
      await updateAppName(appName);
      toast({ title: "App name updated" });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleSavePanelName = async () => {
    setSavingPanel(true);
    try {
      await updateSettings({ panel_name: panelName });
      toast({ title: "Panel name updated" });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingPanel(false);
    }
  };

  const handleSaveBrandKit = async () => {
    setSavingPanel(true);
    try {
      await Promise.all([
        updateAppName(appName),
        updateSettings({ panel_name: panelName }),
      ]);
      toast({ title: "Brand kit saved" });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingPanel(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading...</div>;

  const desktopLogoUrl = getAssetUrl(config?.logo?.desktop);
  const introUrl = getAssetUrl(config?.intro_video?.desktop);

  return (
    <div className="space-y-6">
      <p className="text-slate-600">
        White-label the player and admin panel. Brand Kit gives a single-screen overview; the per-asset tabs let you manage every variant.
      </p>

      <Tabs defaultValue="kit" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="kit" data-testid="branding-tab-kit" className="data-[state=active]:bg-white">
            <Sparkles className="w-3.5 h-3.5 mr-1.5 text-orange-500" />
            Brand Kit
          </TabsTrigger>
          <TabsTrigger value="logos" data-testid="branding-tab-logos" className="data-[state=active]:bg-white">
            <ImageIcon className="w-3.5 h-3.5 mr-1.5" />
            Logos
          </TabsTrigger>
          <TabsTrigger value="intro" data-testid="branding-tab-intro" className="data-[state=active]:bg-white">
            <Video className="w-3.5 h-3.5 mr-1.5" />
            Intro videos
          </TabsTrigger>
        </TabsList>

        {/* ===== Brand Kit overview ===== */}
        <TabsContent value="kit" className="mt-5">
          <Card className="border-slate-200 overflow-hidden">
            {/* Live preview banner */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white px-6 py-8 flex items-center gap-4 border-b border-slate-200">
              {desktopLogoUrl ? (
                <img
                  src={desktopLogoUrl}
                  alt="logo preview"
                  className="w-14 h-14 rounded-xl object-cover bg-white/10 border border-white/10"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                  <FileBox className="w-7 h-7 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] text-orange-300 mb-0.5">
                  Live preview
                </div>
                <div className="text-xl font-bold tracking-tight truncate">
                  {appName || "ZA Player Pro"}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  Admin panel: {panelName || "ZA Player Pro Admin"}
                </div>
              </div>
              <div className="hidden sm:flex flex-col items-end gap-1.5 text-[11px]">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300">
                  {desktopLogoUrl ? "Logo set" : "No logo"}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-sky-500/20 border border-sky-500/40 text-sky-300">
                  {introUrl ? "Intro set" : "No intro"}
                </span>
              </div>
            </div>

            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="flex items-center gap-1.5">
                    <Palette className="w-3.5 h-3.5 text-orange-500" />
                    Player app name
                  </Label>
                  <Input
                    data-testid={ADMIN.appNameInput}
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    placeholder="ZA Player Pro"
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Shown in the player top bar and activation screen.
                  </p>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">
                    <Palette className="w-3.5 h-3.5 text-orange-500" />
                    Admin panel name
                  </Label>
                  <Input
                    data-testid="branding-panel-name-input"
                    value={panelName}
                    onChange={(e) => setPanelName(e.target.value)}
                    placeholder="ZA Player Pro Admin"
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Shown in the admin sidebar and login page.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <AssetCard
                  title="Desktop Logo"
                  hint="1024×1024px, PNG/SVG · shown in the top bar"
                  type="image"
                  url={desktopLogoUrl}
                  testId={ADMIN.logoUploadDesktop}
                  uploading={uploading === "logo-desktop"}
                  onUpload={(file) => handleLogoUpload("desktop", file)}
                  onDelete={() => handleLogoDelete("desktop")}
                />
                <AssetCard
                  title="Intro Video (Desktop)"
                  hint="1920×1080 MP4, &lt;30s · plays once on launch"
                  type="video"
                  url={introUrl}
                  testId={ADMIN.introUploadDesktop}
                  uploading={uploading === "intro-desktop"}
                  onUpload={(file) => handleIntroUpload("desktop", file)}
                  onDelete={() => handleIntroDelete("desktop")}
                />
              </div>

              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button
                  data-testid="branding-save-kit"
                  onClick={handleSaveBrandKit}
                  disabled={savingPanel}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingPanel ? "Saving…" : "Save Brand Kit"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-slate-500 mt-3">
            Looking for mobile-specific variants or icon sizes? See the <span className="font-medium">Logos</span> and <span className="font-medium">Intro videos</span> tabs above.
          </p>
        </TabsContent>

        {/* ===== Logos tab ===== */}
        <TabsContent value="logos" className="mt-5">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">App Name</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-3">
              <Input
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="ZA Player Pro"
                className="max-w-md"
              />
              <Button
                onClick={handleSaveAppName}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </CardContent>
          </Card>
          <div className="mt-5">
            <h2 className="font-semibold text-slate-900 mb-3">Logo Assets</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {logoVariants.map((v) => (
                <AssetCard
                  key={v.key}
                  title={v.label}
                  hint={v.hint}
                  type="image"
                  url={getAssetUrl(config?.logo?.[v.key])}
                  testId={
                    v.key === "desktop"
                      ? ADMIN.logoUploadDesktop
                      : v.key === "mobile"
                      ? ADMIN.logoUploadMobile
                      : ADMIN.logoUploadIcon
                  }
                  uploading={uploading === `logo-${v.key}`}
                  onUpload={(file) => handleLogoUpload(v.key, file)}
                  onDelete={() => handleLogoDelete(v.key)}
                />
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ===== Intro Videos tab ===== */}
        <TabsContent value="intro" className="mt-5">
          <h2 className="font-semibold text-slate-900 mb-1">Intro Videos</h2>
          <p className="text-sm text-slate-500 mb-3">
            Plays on app open (after activation). Users can skip.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {introVariants.map((v) => (
              <AssetCard
                key={v.key}
                title={v.label}
                hint={v.hint}
                type="video"
                url={getAssetUrl(config?.intro_video?.[v.key])}
                testId={
                  v.key === "desktop" ? ADMIN.introUploadDesktop : ADMIN.introUploadMobile
                }
                uploading={uploading === `intro-${v.key}`}
                onUpload={(file) => handleIntroUpload(v.key, file)}
                onDelete={() => handleIntroDelete(v.key)}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
